import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FiX } from 'react-icons/fi';
import { truncate } from 'lodash-es';
import useBucketList from 'src/hooks/useBucketList';
import useBreakpoints from 'src/hooks/useBreakpoints';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import ApiClient from 'src/lib/api_client';
import ImageWithFallback from './ImageWithFallback';
import { Option } from 'src/types';
import dayjs from 'dayjs';
import classNames from 'classnames';
import useInitialData from 'src/hooks/useInitialData';
import * as yup from 'yup';
import qs from 'qs';
import { useTranslations } from 'next-intl';

const ValidationSchema = yup.object().shape({
  displayName: yup
    .string()
    .trim()
    .required('validationDisplayNameRequired')
    .typeError('validationDisplayNameTypeError'),
});

const BucketListModal: React.FC<{
  show: boolean;
  onClose: () => void;
  option: Option;
  isGiftExperience?: boolean;
}> = ({ option, isGiftExperience, show, onClose }) => {
  const translation = useTranslations('components.BucketListModal');
  const validationTranslation = useTranslations('validations');
  const { bucketLists, mutate } = useBucketList();
  const initialData = useInitialData();
  const { isScreenMd } = useBreakpoints();
  const [error, setError] = useState('');
  const [currentBucketListId, setCurrentBucketListId] = useState<number | undefined>(undefined);
  const createBucketList = useEnhanceApiClient(ApiClient.createBucketList);
  const createBucketListOption = useEnhanceApiClient(ApiClient.createBucketListOption);
  const [shouldCreateBucketList, setShouldCreateBucketList] = useState(true);
  const cityName = option.cityName || initialData.cities.find((o) => o.id === option.cityId)?.name;
  const [bucketName, setBucketName] = useState(cityName ? `${cityName} ${dayjs().year()}` : '');

  useEffect(() => {
    if (bucketLists.length > 0) {
      setShouldCreateBucketList(false);
    }
  }, [bucketLists.length]);

  return (
    <Modal
      show={show}
      className="modal-styled modal-styled-sm"
      onHide={() => {
        onClose();
        setError('');
        setBucketName('');
      }}
    >
      <Modal.Header>
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
          <div className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
            {shouldCreateBucketList ? translation('createBucketList') : translation('SaveToBucketList')}
          </div>
          <FiX
            size={24}
            className="tw-cursor-pointer"
            onClick={() => {
              onClose();
              setError('');
              setBucketName('');
            }}
          />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div>
          {shouldCreateBucketList ? (
            <div className="tw-mb-4">
              <label>{translation('name')}</label>
              {isScreenMd ? (
                <input
                  value={bucketName}
                  className="form-control tw-text-black tw-rounded-lg tw-text-brand-navy"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setBucketName(e.target.value);
                  }}
                />
              ) : (
                <textarea
                  rows={5}
                  value={bucketName}
                  className="form-control tw-text-black tw-rounded-lg tw-text-brand-navy"
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setBucketName(e.target.value);
                  }}
                />
              )}

              {error ? (
                <div className="tw-text-red tw-text-sm tw-mt-2">
                  {validationTranslation.has(error) ? validationTranslation(error) : error}
                </div>
              ) : null}
            </div>
          ) : (
            bucketLists.map((bucketList) => (
              <div
                key={bucketList.id}
                onMouseLeave={() => {
                  setCurrentBucketListId(undefined);
                }}
                onMouseEnter={() => {
                  setCurrentBucketListId(bucketList.id);
                }}
                className="tw-flex tw-items-start tw-mb-5 tw-cursor-pointer"
                onClick={() => {
                  createBucketListOption({
                    bucketListId: bucketList.id,
                    optionId: Number(option.id),
                    isGiftExperience: isGiftExperience,
                  })
                    .then((result) => {
                      const [json] = result;
                      if ('error' in json && json.error) {
                        setError(json.error);
                      } else {
                        setError('');
                        mutate();
                        onClose();
                      }
                    })
                    .catch((e) => {
                      if (e instanceof Error) {
                        setError(e.message);
                      }
                    });
                }}
              >
                <div className="tw-mr-4 tw-outline-none">
                  <ImageWithFallback
                    record={bucketList.images[0]}
                    imgField="webpMdUrl"
                    fill
                    alt={bucketList.title}
                    className={classNames(
                      'tw-static tw-object-cover md:tw-w-24 md:tw-h-18 tw-w-20 tw-h-16 tw-border-2 tw-border-solid tw-rounded-lg',
                      {
                        'tw-border-brand-green': currentBucketListId !== bucketList.id,
                        'tw-border-orange-darkest': currentBucketListId === bucketList.id,
                      }
                    )}
                  />
                </div>
                <div>
                  <div
                    className={classNames({
                      'tw-font-semibold tw-underline': currentBucketListId === bucketList.id,
                    })}
                  >
                    {truncate(bucketList.title, { length: isScreenMd ? 100 : 60 })}
                  </div>
                  <div className="tw-text-sm tw-text-grey">{bucketList.optionsCount} {translation('saved')}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {shouldCreateBucketList ? (
          <button
            className="btn btn-primary md:tw-text-base tw-text-sm tw-leading-none md:tw-leading-none"
            disabled={!bucketName}
            onClick={() => {
              let messageError = undefined;
              try {
                ValidationSchema.validateSync(
                  { displayName: bucketName },
                  {
                    abortEarly: false,
                  }
                );
              } catch (error) {
                if (error instanceof yup.ValidationError) {
                  const strError = error.inner
                    .map((obj) => {
                      return qs.stringify(obj.path ? { [obj.path]: obj.message } : {});
                    })
                    .join('&');
                  const objError = qs.parse(strError, { allowDots: true, allowSparse: true });
                  messageError = objError.displayName;
                  setError(messageError as string);
                }
              }

              if (!messageError) {
                createBucketList({
                  title: bucketName,
                  optionId: Number(option.id),
                  isGiftExperience: isGiftExperience,
                })
                  .then((result) => {
                    const [json] = result;
                    if ('error' in json && json.error) {
                      setError(json.error);
                    } else {
                      setError('');
                      setBucketName('');
                      mutate();
                      onClose();
                    }
                  })
                  .catch((e) => {
                    if (e instanceof Error) {
                      setError(e.message);
                    }
                  });
              }
            }}
          >
            {translation('create')}
          </button>
        ) : (
          <button
            className="btn btn-outline tw-bg-white md:tw-text-base tw-text-sm tw-leading-none md:tw-leading-none"
            onClick={() => {
              setShouldCreateBucketList(true);
            }}
          >
            {translation('createNewList')}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BucketListModal;
